/// <reference types="vite/client" />
import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { PingUpdateEvent, StatusChangeEvent } from '../types';

// Socket URL from environment or default to localhost
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3002';

interface UseSocketOptions {
  autoConnect?: boolean;
  onConnect?: () => void;
  onDisconnect?: () => void;
  onError?: (error: Error) => void;
}

interface UseSocketReturn {
  socket: Socket | null;
  isConnected: boolean;
  isConnecting: boolean;
  connect: () => void;
  disconnect: () => void;
  joinMonitor: (monitorId: number) => void;
  leaveMonitor: (monitorId: number) => void;
  onPingUpdate: (callback: (event: PingUpdateEvent) => void) => void;
  onStatusChange: (callback: (event: StatusChangeEvent) => void) => void;
  offPingUpdate: (callback: (event: PingUpdateEvent) => void) => void;
  offStatusChange: (callback: (event: StatusChangeEvent) => void) => void;
}

export const useSocket = (options: UseSocketOptions = {}): UseSocketReturn => {
  const {
    autoConnect = true,
    onConnect,
    onDisconnect,
    onError,
  } = options;

  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  // Store callback refs to avoid dependency issues
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onErrorRef = useRef(onError);

  // Update refs when callbacks change
  useEffect(() => {
    onConnectRef.current = onConnect;
    onDisconnectRef.current = onDisconnect;
    onErrorRef.current = onError;
  }, [onConnect, onDisconnect, onError]);

  // Initialize socket connection
  const initializeSocket = useCallback(() => {
    if (socketRef.current) {
      return socketRef.current;
    }

    console.log('Initializing Socket.IO connection to:', SOCKET_URL);
    
    const socket = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
    });

    // Connection event handlers
    socket.on('connect', () => {
      console.log('Socket.IO connected:', socket.id);
      setIsConnected(true);
      setIsConnecting(false);
      onConnectRef.current?.();
    });

    socket.on('disconnect', (reason) => {
      console.log('Socket.IO disconnected:', reason);
      setIsConnected(false);
      setIsConnecting(false);
      onDisconnectRef.current?.();
    });

    socket.on('connect_error', (error) => {
      console.error('Socket.IO connection error:', error);
      setIsConnecting(false);
      onErrorRef.current?.(error);
    });

    socket.on('reconnect', (attemptNumber) => {
      console.log('Socket.IO reconnected after', attemptNumber, 'attempts');
      setIsConnected(true);
      setIsConnecting(false);
    });

    socket.on('reconnect_error', (error) => {
      console.error('Socket.IO reconnection error:', error);
      onErrorRef.current?.(error);
    });

    socketRef.current = socket;
    return socket;
  }, []); // Empty dependency array since we use refs for callbacks

  // Connect to socket
  const connect = useCallback(() => {
    if (isConnected || isConnecting) {
      return;
    }

    setIsConnecting(true);
    const socket = initializeSocket();
    socket.connect();
  }, [isConnected, isConnecting, initializeSocket]);

  // Disconnect from socket
  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
      setIsConnecting(false);
    }
  }, []);

  // Join monitor room
  const joinMonitor = useCallback((monitorId: number) => {
    if (socketRef.current && isConnected) {
      console.log('Joining monitor room:', monitorId);
      socketRef.current.emit('join_monitor', monitorId);
    }
  }, [isConnected]);

  // Leave monitor room
  const leaveMonitor = useCallback((monitorId: number) => {
    if (socketRef.current && isConnected) {
      console.log('Leaving monitor room:', monitorId);
      socketRef.current.emit('leave_monitor', monitorId);
    }
  }, [isConnected]);

  // Listen for ping updates
  const onPingUpdate = useCallback((callback: (event: PingUpdateEvent) => void) => {
    if (socketRef.current) {
      socketRef.current.on('ping_update', callback);
    }
  }, []);

  // Listen for status changes
  const onStatusChange = useCallback((callback: (event: StatusChangeEvent) => void) => {
    if (socketRef.current) {
      socketRef.current.on('status_change', callback);
    }
  }, []);

  // Remove ping update listener
  const offPingUpdate = useCallback((callback: (event: PingUpdateEvent) => void) => {
    if (socketRef.current) {
      socketRef.current.off('ping_update', callback);
    }
  }, []);

  // Remove status change listener
  const offStatusChange = useCallback((callback: (event: StatusChangeEvent) => void) => {
    if (socketRef.current) {
      socketRef.current.off('status_change', callback);
    }
  }, []);

  // Auto-connect on mount - only run once
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, []); // Empty dependency array to run only once

  return {
    socket: socketRef.current,
    isConnected,
    isConnecting,
    connect,
    disconnect,
    joinMonitor,
    leaveMonitor,
    onPingUpdate,
    onStatusChange,
    offPingUpdate,
    offStatusChange,
  };
}; 